/**
 * Cron-expression presets mirrored from `@nestjs/schedule`'s
 * `CronExpression` enum. Kept as a hand-maintained const map because
 * `api-contracts` has no `@nestjs/*` dep — importing the enum would
 * cascade the whole NestJS runtime into the FE.
 *
 * Backend + FE consume this same source: the sadmin cron-control API
 * validates PUT bodies against `code` (when the operator picked a
 * preset) and echoes the `expression` back; the FE dropdown renders
 * `label`; the FE "next run" preview parses `expression` with
 * `cron-parser` client-side.
 *
 * When @nestjs/schedule adds a preset, add it here in the same PR.
 */
export interface CronExpressionPreset {
  /** Matches the `CronExpression.XXX` enum name. Stable — never rename. */
  code: string;
  /** English label — FE renders the localized version via i18n. */
  label: string;
  /**
   * 6-field crontab expression (seconds first) — same format
   * `SchedulerRegistry.addCronJob` + `cron-parser` accept.
   */
  expression: string;
  /** Interval in seconds — used by the FE to hide sub-min presets when the cron's minIntervalSeconds is > 60. */
  intervalSeconds: number;
}

export const CRON_EXPRESSION_PRESETS: CronExpressionPreset[] = [
  {
    code: 'EVERY_SECOND',
    label: 'Every second',
    expression: '* * * * * *',
    intervalSeconds: 1,
  },
  {
    code: 'EVERY_5_SECONDS',
    label: 'Every 5 seconds',
    expression: '*/5 * * * * *',
    intervalSeconds: 5,
  },
  {
    code: 'EVERY_10_SECONDS',
    label: 'Every 10 seconds',
    expression: '*/10 * * * * *',
    intervalSeconds: 10,
  },
  {
    code: 'EVERY_30_SECONDS',
    label: 'Every 30 seconds',
    expression: '*/30 * * * * *',
    intervalSeconds: 30,
  },
  {
    code: 'EVERY_MINUTE',
    label: 'Every minute',
    expression: '0 */1 * * * *',
    intervalSeconds: 60,
  },
  {
    code: 'EVERY_5_MINUTES',
    label: 'Every 5 minutes',
    expression: '0 */5 * * * *',
    intervalSeconds: 300,
  },
  {
    code: 'EVERY_10_MINUTES',
    label: 'Every 10 minutes',
    expression: '0 */10 * * * *',
    intervalSeconds: 600,
  },
  {
    code: 'EVERY_15_MINUTES',
    label: 'Every 15 minutes',
    expression: '0 */15 * * * *',
    intervalSeconds: 900,
  },
  {
    code: 'EVERY_30_MINUTES',
    label: 'Every 30 minutes',
    expression: '0 */30 * * * *',
    intervalSeconds: 1800,
  },
  {
    code: 'EVERY_HOUR',
    label: 'Every hour',
    expression: '0 0 */1 * * *',
    intervalSeconds: 3600,
  },
  {
    code: 'EVERY_2_HOURS',
    label: 'Every 2 hours',
    expression: '0 0 */2 * * *',
    intervalSeconds: 7200,
  },
  {
    code: 'EVERY_3_HOURS',
    label: 'Every 3 hours',
    expression: '0 0 */3 * * *',
    intervalSeconds: 10800,
  },
  {
    code: 'EVERY_4_HOURS',
    label: 'Every 4 hours',
    expression: '0 0 */4 * * *',
    intervalSeconds: 14400,
  },
  {
    code: 'EVERY_5_HOURS',
    label: 'Every 5 hours',
    expression: '0 0 */5 * * *',
    intervalSeconds: 18000,
  },
  {
    code: 'EVERY_6_HOURS',
    label: 'Every 6 hours',
    expression: '0 0 */6 * * *',
    intervalSeconds: 21600,
  },
  {
    code: 'EVERY_7_HOURS',
    label: 'Every 7 hours',
    expression: '0 0 */7 * * *',
    intervalSeconds: 25200,
  },
  {
    code: 'EVERY_8_HOURS',
    label: 'Every 8 hours',
    expression: '0 0 */8 * * *',
    intervalSeconds: 28800,
  },
  {
    code: 'EVERY_9_HOURS',
    label: 'Every 9 hours',
    expression: '0 0 */9 * * *',
    intervalSeconds: 32400,
  },
  {
    code: 'EVERY_10_HOURS',
    label: 'Every 10 hours',
    expression: '0 0 */10 * * *',
    intervalSeconds: 36000,
  },
  {
    code: 'EVERY_11_HOURS',
    label: 'Every 11 hours',
    expression: '0 0 */11 * * *',
    intervalSeconds: 39600,
  },
  {
    code: 'EVERY_12_HOURS',
    label: 'Every 12 hours',
    expression: '0 0 */12 * * *',
    intervalSeconds: 43200,
  },
  {
    code: 'EVERY_DAY_AT_1AM',
    label: 'Every day at 1am',
    expression: '0 0 01 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_2AM',
    label: 'Every day at 2am',
    expression: '0 0 02 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_3AM',
    label: 'Every day at 3am',
    expression: '0 0 03 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_4AM',
    label: 'Every day at 4am',
    expression: '0 0 04 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_5AM',
    label: 'Every day at 5am',
    expression: '0 0 05 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_6AM',
    label: 'Every day at 6am',
    expression: '0 0 06 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_7AM',
    label: 'Every day at 7am',
    expression: '0 0 07 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_8AM',
    label: 'Every day at 8am',
    expression: '0 0 08 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_9AM',
    label: 'Every day at 9am',
    expression: '0 0 09 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_10AM',
    label: 'Every day at 10am',
    expression: '0 0 10 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_11AM',
    label: 'Every day at 11am',
    expression: '0 0 11 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_NOON',
    label: 'Every day at noon',
    expression: '0 0 12 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_1PM',
    label: 'Every day at 1pm',
    expression: '0 0 13 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_2PM',
    label: 'Every day at 2pm',
    expression: '0 0 14 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_3PM',
    label: 'Every day at 3pm',
    expression: '0 0 15 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_4PM',
    label: 'Every day at 4pm',
    expression: '0 0 16 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_5PM',
    label: 'Every day at 5pm',
    expression: '0 0 17 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_6PM',
    label: 'Every day at 6pm',
    expression: '0 0 18 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_7PM',
    label: 'Every day at 7pm',
    expression: '0 0 19 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_8PM',
    label: 'Every day at 8pm',
    expression: '0 0 20 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_9PM',
    label: 'Every day at 9pm',
    expression: '0 0 21 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_10PM',
    label: 'Every day at 10pm',
    expression: '0 0 22 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_11PM',
    label: 'Every day at 11pm',
    expression: '0 0 23 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_DAY_AT_MIDNIGHT',
    label: 'Every day at midnight',
    expression: '0 0 00 * * *',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_WEEK',
    label: 'Every week (Sunday midnight)',
    expression: '0 0 00 * * 0',
    intervalSeconds: 604800,
  },
  {
    code: 'EVERY_WEEKDAY',
    label: 'Every weekday (Mon-Fri midnight)',
    expression: '0 0 00 * * 1-5',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_WEEKEND',
    label: 'Every weekend (Sat & Sun midnight)',
    expression: '0 0 00 * * 6,0',
    intervalSeconds: 86400,
  },
  {
    code: 'MONDAY_TO_FRIDAY_AT_1AM',
    label: 'Weekdays at 1am',
    expression: '0 0 01 * * 1-5',
    intervalSeconds: 86400,
  },
  {
    code: 'MONDAY_TO_FRIDAY_AT_2AM',
    label: 'Weekdays at 2am',
    expression: '0 0 02 * * 1-5',
    intervalSeconds: 86400,
  },
  {
    code: 'MONDAY_TO_FRIDAY_AT_9AM',
    label: 'Weekdays at 9am',
    expression: '0 0 09 * * 1-5',
    intervalSeconds: 86400,
  },
  {
    code: 'EVERY_1ST_DAY_OF_MONTH_AT_MIDNIGHT',
    label: '1st of the month at midnight',
    expression: '0 0 00 1 * *',
    intervalSeconds: 2592000,
  },
  {
    code: 'EVERY_1ST_DAY_OF_MONTH_AT_NOON',
    label: '1st of the month at noon',
    expression: '0 0 12 1 * *',
    intervalSeconds: 2592000,
  },
  {
    code: 'EVERY_QUARTER',
    label: 'Every quarter (1st of quarter, midnight)',
    expression: '0 0 00 1 */3 *',
    intervalSeconds: 7776000,
  },
];

/** Fast lookup by preset code. */
export const CRON_EXPRESSION_PRESET_BY_CODE: Readonly<
  Record<string, CronExpressionPreset>
> = Object.freeze(
  CRON_EXPRESSION_PRESETS.reduce<Record<string, CronExpressionPreset>>(
    (acc, p) => {
      acc[p.code] = p;
      return acc;
    },
    {}
  )
);
